import { ErrorTypes, QueryObjectTags } from '@fabric-ui/components';
import { noop, QueryKey } from '@fabric-ui/utils';
import { QueryClient, queryOptions, type dataTagSymbol, type UseQueryResult } from '@tanstack/react-query';
import { gql, GraphQLResponse } from 'graphql-request';
import { type AvContextType } from '../../context/types';
import { QueryObject } from '../../views/CustomDashboards/types';
import { CANCEL_ASYNC_QUERY } from '../useQuerySql';
import { type QueryObjectProps } from './useQueryObjectAsyncWithCachedResults';

type Row = {
  data?: Record<string, unknown>;
  id: string;
};

export type DwQueryResponse = {
  asyncResult?: 'ASYNC_DONE' | 'ASYNC_RUNNING';
  queryId?: string;
  shouldPollWithQueryId?: boolean | null;
  responseCalculatedAt?: string | null;
  totalRowCount?: number | null;
  totals?: Row | Row[] | null;
  rows?: Row[];
};

export type AsyncQueryWithCacheResponse = {
  queryId?: string;
  responseCalculatedAt?: string | null;
  shouldPollWithQueryId?: boolean | null;
  maybeResponse?: DwQueryResponse | null;
};

export const QUERY = gql`
  query queryObjectAsync($req: Json!) {
    queryObjectAsync(value: $req) {
      queryId
      shouldPollWithQueryId
      responseCalculatedAt
      maybeResponse {
        asyncResult
        queryId
        rows {
          data
          id
        }
        totalRowCount
        totals {
          data
          id
        }
      }
    }
  }
`;

export const POLLING = gql`
  query asyncPollDwQuery($req: Json!) {
    asyncPollDwQuery(value: $req) {
      rows {
        data
        id
      }
      totalRowCount
      totals {
        data
        id
      }
      asyncResult
    }
  }
`;

export const POLLING_INTERVAL = 1;

export enum AsyncQueryStatus {
  /** Loading initial query object */
  Loading = 'Loading',
  /** Polling for data when no stale/fresh data received */
  Polling = 'Polling',
  /** Received stale data and polling for fresh data */
  Refreshing = 'Refreshing',
  /** Polling completed or initial response data was fresh */
  Done = 'Done',
  /** Query not started */
  Idle = 'Idle',
}

export const formatTags = (tags?: QueryObjectTags) =>
  tags
    ? Object.entries(tags)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => `${key}:${value}`)
    : undefined;

/**
 * The `queryId` used to poll `asyncPollDwQuery` is a base64-encoded JSON token (`AsyncQueryMetadataToken`
 * on the server) that carries, among other things, the Avalor query ID used to look up the query in Snowflake's
 * query history. This decodes that token and extracts `avalorQueryId`.
 */
export const decodeAvalorQueryId = (queryId?: string): string | undefined => {
  if (!queryId) {
    return undefined;
  }
  try {
    return JSON.parse(atob(queryId))?.avalorQueryId ?? undefined;
  } catch {
    return undefined;
  }
};

export const getAsyncQueryStatus = (
  asyncPollQuery: UseQueryResult<ReturnType<typeof getAsyncPollDwQueryQueryOptions>['queryKey'][typeof dataTagSymbol]>,
  queryObjectAsyncQuery: UseQueryResult<ReturnType<typeof getQueryObjectAsyncQueryOptions>['queryKey'][typeof dataTagSymbol]>,
  showStaleData = false
) => {
  if (
    asyncPollQuery.data?.asyncResult === 'ASYNC_DONE' ||
    (!queryObjectAsyncQuery.data?.shouldPollWithQueryId && queryObjectAsyncQuery.data?.maybeResponse)
  ) {
    return AsyncQueryStatus.Done;
  }

  if (queryObjectAsyncQuery.data?.shouldPollWithQueryId && queryObjectAsyncQuery.data?.maybeResponse && showStaleData) {
    return AsyncQueryStatus.Refreshing;
  }

  if (asyncPollQuery.data?.asyncResult === 'ASYNC_RUNNING' || asyncPollQuery.isLoading) {
    return AsyncQueryStatus.Polling;
  }

  if (queryObjectAsyncQuery.isLoading) {
    return AsyncQueryStatus.Loading;
  }

  return AsyncQueryStatus.Idle;
};

export const selectDataFromResults = (
  asyncPollQuery: UseQueryResult<ReturnType<typeof getAsyncPollDwQueryQueryOptions>['queryKey'][typeof dataTagSymbol]>,
  queryObjectAsyncQuery: UseQueryResult<ReturnType<typeof getQueryObjectAsyncQueryOptions>['queryKey'][typeof dataTagSymbol]>,
  showStaleData = false
) => {
  const isStaleCachedData = Boolean(queryObjectAsyncQuery.data?.shouldPollWithQueryId && queryObjectAsyncQuery.data?.maybeResponse);
  const isFreshCachedData = Boolean(!queryObjectAsyncQuery.data?.shouldPollWithQueryId && queryObjectAsyncQuery.data?.maybeResponse);

  if (asyncPollQuery.data?.asyncResult === 'ASYNC_DONE') {
    return asyncPollQuery.data;
  }
  if (isFreshCachedData) {
    return queryObjectAsyncQuery.data?.maybeResponse;
  }
  if (showStaleData && isStaleCachedData) {
    return queryObjectAsyncQuery.data?.maybeResponse;
  }

  return asyncPollQuery.data;
};

type GetQueryObjectAsyncIdQueryOptionsProps = Omit<QueryObjectProps, 'queryObject'> & {
  query: QueryObject;
  api: AvContextType['api'];
  isCacheEnabled?: boolean;
};
export const getQueryObjectAsyncQueryOptions = ({
  api,
  query,
  origin,
  showTotals,
  fileFormat,
  searchQuery,
  contextFilter,
  totalRowCount,
  aliasResolution,
  tags,
  featureFlagsOverrides,
  isCacheEnabled = true,
  staleDataTtlDays,
}: GetQueryObjectAsyncIdQueryOptionsProps) =>
  queryOptions({
    queryKey: [QueryKey.QueryObjectAsync, query, contextFilter, showTotals, totalRowCount, featureFlagsOverrides, staleDataTtlDays],
    queryFn: ({ signal }): Promise<AsyncQueryWithCacheResponse> =>
      api(QUERY, {
        options: {
          req: {
            query,
            totals: { totalRow: showTotals, totalRowCount },
            origin,
            fileFormat,
            contextFilter,
            aliasResolution,
            searchQuery,
            isCacheEnabled,
            tags: formatTags(tags),
            featureFlagsOverrides,
            staleDataTtlDays,
          },
        },
        signal,
      })
        .then((data: GraphQLResponse<{ queryObjectAsync: AsyncQueryWithCacheResponse }>) => {
          if (data === undefined || data.errors) {
            throw data?.errors || new Error(`${ErrorTypes.Network}: ${query}`);
          }
          return data.data?.queryObjectAsync;
        })
        .catch(e => {
          if (!signal?.aborted) {
            throw e;
          }
        }),

    select: result => ({
      ...result,
      maybeResponse: result.maybeResponse?.rows
        ? {
            ...result.maybeResponse,
            rows: result.maybeResponse?.rows?.map(row => ({ ...row.data, id: row.id })),
          }
        : result.maybeResponse || undefined,
    }),
  });

type GetAsyncPollDwQueryQueryOptions = {
  api: AvContextType['api'];
  queryId?: string;
  onError?: (e: unknown) => void;
  queryClient: QueryClient;
  muteErrors?: boolean;
};
export const getAsyncPollDwQueryQueryOptions = ({ api, queryId, onError, queryClient, muteErrors }: GetAsyncPollDwQueryQueryOptions) =>
  queryOptions({
    queryKey: [QueryKey.AsyncPollDwQuery, queryId],
    queryFn: ({ signal }): Promise<DwQueryResponse> =>
      api(POLLING, {
        options: { req: { queryId } },
        signal,
        muteErrors,
      })
        .then((data: GraphQLResponse<{ asyncPollDwQuery: DwQueryResponse }>) => {
          if (data === undefined || data.errors) {
            throw data?.errors || new Error(`${ErrorTypes.Network}: ${queryId}`);
          }
          return {
            ...data.data?.asyncPollDwQuery,
            rows: data.data?.asyncPollDwQuery.asyncResult === 'ASYNC_DONE' ? data.data.asyncPollDwQuery.rows : undefined,
          };
        })
        .catch(e => {
          if (signal.aborted) {
            api(CANCEL_ASYNC_QUERY, { options: { queryId }, muteErrors: true }).catch(noop);
            queryClient.resetQueries({
              predicate: ({ state: { data } }) =>
                Boolean(data && typeof data === 'object' && 'queryId' in data && data.queryId === queryId),
            });
            return;
          }
          onError?.(e);
          throw e;
        }),

    select: result => (result.rows ? { ...result, rows: result.rows.map(row => ({ ...row.data, id: row.id })) } : result),
    retry: false,
    refetchInterval: query => {
      const { asyncResult } = query.state.data || {};
      if (asyncResult === 'ASYNC_RUNNING' && !query.state.error) {
        return POLLING_INTERVAL;
      }
      return false;
    },
  });
